import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { Button, Form, Modal, Spinner, OverlayTrigger, Tooltip } from 'react-bootstrap';
import CreatableSelect from 'react-select/creatable';
import validator from 'validator';
import { postRequest } from '../../helpers/apiHandler';
import config from '../../config/Constant';
import CustomAlert from '../../components/CustomAlert';
import { GreenIconSVG } from '../SVG/GreenIconSVG';
import CloseIcon from '../SVG/CloseIcon';
import { reloadContactList, reloadContactOverview } from '../../store/actions/reduxAction';
import InfoIcon from '../../assets/icon-info.svg';
import { MultiSelectStyle } from '../../helpers/MultiSelectStyle';

const ContactAddContactList = props => {
  const {
    openModalSegment,
    setOpenModalSegment,
    allTags,
    access_token,
    reloadContactList,
    reloadContactOverview,
  } = props;

  const [loading, setLoading] = useState(false);
  const [tagOptions, setTagOptions] = useState([]);
  const [contactListName, setContactListName] = useState('');
  const [selectedTags, setSelectedTags] = useState([]);
  const [contactListNameError, setContactListNameError] = useState({ error: false, message: '' });

  const [alertState, setAlertState] = useState({
    show: false,
    variant: 'danger',
    message: '',
  });

  const handleCloseModal = () => {
    setOpenModalSegment(false);
    // Reset form fields
    setContactListName('');
    setSelectedTags([]);
  };

  const handleAddContact = event => {
    event.preventDefault();
    const form = event.currentTarget;

    if (form.checkValidity() === false) {
      event.stopPropagation();
    }

    if (!validator.isLength(contactListName.trim(), { min: 2, max: 30 })) {
      setContactListNameError({
        error: true,
        message: 'Contact list name must be between 2 and 30 characters long.',
      });
      return;
    }

    setLoading(true);
    const data = {
      name: contactListName,
      tags: selectedTags.map(tag => tag.value),
    };

    postRequest('/v2/contacts-add-contact-list', data, access_token.token, res => {
      if (res.success) {
        // Handle success if needed
        handleCloseModal();
        reloadContactList();
        reloadContactOverview();
        setAlertState({
          message: 'New Contact List saved successfully',
          show: true,
          variant: 'success',
        });
      } else {
        let errMsg = 'There was an error saving new contact list!';
        if (res.code === 12200) {
          errMsg = 'Contact List name must be unique';
        }
        setAlertState({
          message: errMsg,
          show: true,
          variant: 'danger',
        });
      }
      setLoading(false);
    });
  };

  useEffect(() => {
    if (openModalSegment === true) {
      setTagOptions(allTags);
    }
  }, [openModalSegment]);

  useEffect(() => {
    if (alertState.show) {
      setTimeout(() => {
        setAlertState({
          message: '',
          show: false,
          variant: '',
        });
      }, config.notify_time);
    }
  }, [alertState]);

  return (
    <>
      <CustomAlert
        message={alertState.message}
        show={alertState.show}
        variant={alertState.variant}
        closeAlert={() => setAlertState({ show: false, variant: 'danger', message: '' })}
      />
      <Modal
        show={openModalSegment}
        animation={true}
        onHide={handleCloseModal}
        className="modal-form-contact"
        centered
      >
        <Modal.Body>
          <CloseIcon handleModalClose={handleCloseModal} />
          <GreenIconSVG />
          <Modal.Title as="h4" style={{ margin: '8px 0 16px' }}>
            Create Contact List
          </Modal.Title>
          <p>Use Contact Lists to organize your contacts into groups. Ensure the Contact List Name is unique.</p>
          <Form noValidate onSubmit={handleAddContact}>
            <Form.Group>
              <Form.Label>Contact List Name</Form.Label>
              <Form.Control
                maxLength={30}
                value={contactListName}
                onChange={(e) => setContactListName(e.target.value)}
                isInvalid={contactListNameError.error}
              />
              <Form.Control.Feedback type="invalid">
                {contactListNameError.message}
              </Form.Control.Feedback>
            </Form.Group>
            <Form.Group>
              <Form.Label>Select Tag(s)</Form.Label>
              <OverlayTrigger
                placement="right"
                overlay={
                  <Tooltip id="tooltip-name">
                    Add tagged contact(s) by selecting one or more pre-made tags from the dropdown menu.
                  </Tooltip>
                }
              >
                <span className="ml-2"><img src={InfoIcon} style={{ width: '14px' }}></img></span>
              </OverlayTrigger>
              <CreatableSelect
                options={tagOptions.map((tag) => ({ value: tag, label: tag }))}
                isMulti
                value={selectedTags}
                onChange={setSelectedTags}
                styles={MultiSelectStyle}
                placeholder={tagOptions && tagOptions.length > 0 ? "Select..." : "No saved tags"}
              />
            </Form.Group>
            <Form.Group>
              <Button type="submit" variant="primary" disabled={loading} style={{ minWidth: 160 }}>
                {loading && <Spinner animation="border" size="sm" className="mr-2" />}
                Save
              </Button>
            </Form.Group>
          </Form>
        </Modal.Body>
      </Modal>
    </>
  );
};

const mapStateToProps = state => {
  const { access_token, user } = state.auth;

  return {
    access_token,
    user,
  };
};

const mapDispatchToProps = dispatch => ({
  reloadContactList: () => dispatch(reloadContactList()),
  reloadContactOverview: () => dispatch(reloadContactOverview()),
});

export default connect(mapStateToProps, mapDispatchToProps)(ContactAddContactList);
